import React from "react";
import PropTypes from "prop-types";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import "swiper/css/effect-fade";
import Image from "../image/image";
import styles from "./imageVideoSlider.module.less";

const ImageVideoSlider = ({
  dataList = [],
  width = 200,
  height = 125,
  showNavigation = true,
  showPagination = false,
  autoplay = false,
  autoplayDelay = 3000,
  loop = false,
  slidesPerView = "auto",
  spaceBetween = 20,
  className = "",
  renderPreviewMask = null, // New prop for custom preview mask
  onSlideChange = () => {},
  onSwiper = () => {},
  onClickSchema = () => {}, // New prop for schema click callback
}) => {
  // Helper function to get YouTube embed code
  const getYouTubeEmbedCode = (url) => {
    if (!url) return "";

    if (url?.includes("/embed/")) {
      return url;
    }

    const watchUrlMatch = url?.match(/[?&]v=([^&]+)/);
    const watchVideoId = (watchUrlMatch && watchUrlMatch[1]) || "";
    if (watchVideoId) {
      return `https://www.youtube.com/embed/${watchVideoId}`;
    }

    const videoUrlMatch = url?.match(/\/watch\?v=([^&]+)/);
    const videoId = (videoUrlMatch && videoUrlMatch[1]) || "";
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`;
    }
    return url;
  };

  // Helper function to get embedded code for podcasts
  const getEmbeddedCode = (url, imageURL) => {
    const source = url?.length ? url : imageURL;
    if (!source) return "";

    // Regular expression to extract the URL from src attribute
    const regex = /src="([^"]+)"/;
    const match = source?.match(regex);
    let srcUrl = "";

    if (match) {
      srcUrl = match[1];
    } else {
      srcUrl = source; // If no src attribute, use the source directly
    }
    return srcUrl;
  };

  const renderSlide = (item, index) => {
    const {
      src,
      url,
      ImageURL,
      VideoIframe,
      type,
      alt = `Slide ${index + 1}`,
      ...restProps
    } = item;

    // Handle licenseImage type
    if (
      type === "licenseImage" ||
      type === "image" ||
      type?.includes("image") ||
      type?.includes("Image")
    ) {
      const imageSrc = src || url || ImageURL;
      return (
        <Image
          src={imageSrc}
          alt={alt}
          height="100%"
          width="100%"
          style={{
            objectFit: "cover",
            borderRadius: "8px",
          }}
          preview={
            renderPreviewMask
              ? {
                  mask: renderPreviewMask(item, index),
                  visible: false, // Disable default preview modal
                }
              : true // Completely disable preview if no custom mask
          }
          {...restProps}
        />
      );
    }

    // Handle licenseVideo type
    if (
      type === "licenseVideo" ||
      type === "video" ||
      type?.includes("video") ||
      type?.includes("Video")
    ) {
      const videoSrc = src || url || VideoIframe;
      const embedUrl = getYouTubeEmbedCode(videoSrc);

      return (
        <iframe
          id={`${index}-video`}
          type="text/html"
          src={embedUrl}
          frameBorder="0"
          height="100%"
          width="100%"
          allow="fullscreen"
          style={{
            borderRadius: "8px",
            border: "none",
          }}
          title={`Video ${index + 1}`}
        />
      );
    }

    // Handle licensePodcast type
    if (
      type === "licensePodcast" ||
      type === "podcast" ||
      type?.includes("podcast") ||
      type?.includes("Podcast")
    ) {
      const podcastSrc = src || url || ImageURL;
      const embedUrl = getEmbeddedCode(podcastSrc, ImageURL);

      return (
        <iframe
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "8px",
            border: "none",
          }}
          title="Podcast Player"
          src={embedUrl}
          frameBorder="0"
          allowTransparency="true"
          allow="encrypted-media"
          loading="eager"
          scrolling="no"
        />
      );
    }

    // Handle schema type
    if (
      type === "schema" ||
      type?.includes("schema") ||
      type?.includes("Schema")
    ) {
      const schemaTitle = item?.title || `Schema ${index + 1}`;

      return (
        <div
          className={styles.schemaSlide}
          onClick={() => onClickSchema(item, index)}
          style={{
            width: "100%",
            height: "100%",
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f0f8ff",
            borderRadius: "8px",
            border: "1px solid #d6e4ff",
            padding: "16px",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#e6f4ff";
            e.currentTarget.style.borderColor = "#91caff";
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow =
              "0 4px 12px rgba(24, 144, 255, 0.15)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#f0f8ff";
            e.currentTarget.style.borderColor = "#d6e4ff";
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              color: "#1890ff",
              marginBottom: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z" />
            </svg>
          </div>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#262626",
              textAlign: "center",
              marginBottom: "8px",
            }}
          >
            {schemaTitle}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "#595959",
              textAlign: "center",
            }}
          >
            Click to view details
          </div>
        </div>
      );
    }

    // Fallback for unknown types - try to render as image
    const fallbackSrc = src || url || ImageURL;
    if (fallbackSrc) {
      return (
        <Image
          src={fallbackSrc}
          alt={alt}
          height="100%"
          width="100%"
          style={{
            objectFit: "cover",
            borderRadius: "8px",
          }}
          preview={
            renderPreviewMask
              ? {
                  mask: renderPreviewMask(item, index),
                  visible: false, // Disable default preview modal
                }
              : true // Completely disable preview if no custom mask
          }
          {...restProps}
        />
      );
    }

    // If no valid content, show placeholder
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
          borderRadius: "8px",
          color: "#999",
          fontSize: "14px",
        }}
      >
        No content available
      </div>
    );
  };

  return (
    <div className={`${styles.sliderContainer} ${className}`}>
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        spaceBetween={spaceBetween}
        slidesPerView={slidesPerView}
        navigation={showNavigation}
        pagination={showPagination ? { clickable: true } : false}
        autoplay={
          autoplay
            ? { delay: autoplayDelay, disableOnInteraction: false }
            : false
        }
        loop={loop}
        onSlideChange={onSlideChange}
        onSwiper={onSwiper}
        className={styles.swiper}
        style={{
          "--swiper-navigation-color": "#1890ff",
          "--swiper-pagination-color": "#1890ff",
        }}
      >
        {dataList.map((item, index) => (
          <SwiperSlide key={index} style={{ width, height }}>
            <div className={styles.slideContent}>
              {renderSlide(item, index)}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

ImageVideoSlider.propTypes = {
  dataList: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string,
      url: PropTypes.string,
      ImageURL: PropTypes.string,
      VideoIframe: PropTypes.string,
      type: PropTypes.string.isRequired,
      alt: PropTypes.string,
      // Schema specific props
      data: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
      json: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
      title: PropTypes.string,
    })
  ).isRequired,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showNavigation: PropTypes.bool,
  showPagination: PropTypes.bool,
  autoplay: PropTypes.bool,
  autoplayDelay: PropTypes.number,
  loop: PropTypes.bool,
  slidesPerView: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  spaceBetween: PropTypes.number,
  className: PropTypes.string,
  renderPreviewMask: PropTypes.func, // Function that returns JSX for custom preview mask
  onSlideChange: PropTypes.func,
  onSwiper: PropTypes.func,
  onClickSchema: PropTypes.func, // Function that handles schema click
};

ImageVideoSlider.defaultProps = {
  dataList: [],
  width: 300,
  height: 200,
  showNavigation: true,
  showPagination: false,
  autoplay: false,
  autoplayDelay: 3000,
  loop: false,
  slidesPerView: "auto",
  spaceBetween: 20,
  className: "",
  renderPreviewMask: null,
  onSlideChange: () => {},
  onSwiper: () => {},
  onClickSchema: () => {},
};

export default ImageVideoSlider;
